import { UnifiedStatusSelector } from '@/components/global/selectors/UnifiedStatusSelector';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/shadcn/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { type StatusOption } from '@/types';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

interface InlineStatusCellProps {
    value: string;
    onUpdate?: (value: string) => Promise<void>;
    className?: string;
    statuses?: StatusOption[];
    readOnly?: boolean;
}

export function InlineStatusCell({ value, onUpdate, className, statuses = [], readOnly = false }: InlineStatusCellProps) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSelect = async (newValue: string) => {
        if (newValue === value || !onUpdate) {
            setIsOpen(false);
            return;
        }

        setIsLoading(true);
        try {
            await onUpdate(newValue);
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to update status:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const options = statuses.map((status) => ({ value: status.value, label: status.label }));
    const hasOptions = options.length > 0;

    // Read-only mode: just display the status
    if (readOnly || !onUpdate || !hasOptions) {
        return (
            <div className={cn('flex w-full items-center justify-start px-2 py-1.5', className)}>
                <UnifiedStatusSelector value={value} statusType="project" mode="readonly" />
            </div>
        );
    }

    if (isMobile) {
        return (
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogTrigger asChild>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsOpen(true);
                        }}
                        className={cn('hover:bg-accent w-full rounded px-2 py-1.5 transition-colors', 'flex items-center justify-start', className)}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                        ) : (
                            <UnifiedStatusSelector value={value} statusType="project" mode="readonly" />
                        )}
                    </button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
                    <DialogHeader>
                        <DialogTitle>{t('projects.index.columns.status', 'Status')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-2">
                        {options.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => handleSelect(option.value)}
                                className="bg-background hover:bg-accent flex w-full items-center justify-start rounded-md border px-3 py-2 text-left"
                            >
                                <UnifiedStatusSelector value={option.value} statusType="project" mode="readonly" />
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(true);
                    }}
                    className={cn('hover:bg-accent w-full rounded px-2 py-1.5 transition-colors', 'flex items-center justify-start', className)}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                    ) : (
                        <UnifiedStatusSelector value={value} statusType="project" mode="readonly" />
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
                {options.map((option) => (
                    <DropdownMenuItem key={option.value} onClick={() => handleSelect(option.value)} className="cursor-pointer">
                        <UnifiedStatusSelector value={option.value} statusType="project" mode="readonly" />
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
